import { createSlice } from '@reduxjs/toolkit';
import { translateEntityIds } from '~SpectateStore/thunks/translateEntityIds';

export const SLICE_NAME = 'translatedEntityIds';

export const getDirectionKey = (from, to) => `${from}:${to}`;

export const createDirectionMaps = () => ({
    events: {},
    selections: {},
    inFlight: { events: {}, selections: {} },
});

export const EMPTY_DIRECTION_MAPS = createDirectionMaps();

const initialState = {};

// We never pre-populate directions: initialState is {} and each "from:to" bucket is
// created on demand. So before any reducer can read or write a direction's maps, we
// have to make sure that bucket actually exists — otherwise we'd be reading/writing
// properties on undefined. ensureDirection does exactly that: create the bucket if it's
// missing, then return it so the reducer can safely mutate it (reducers run under Immer).
const ensureDirection = (state, directionKey) => {
    if (!state[directionKey]) {
        state[directionKey] = createDirectionMaps();
    }

    return state[directionKey];
};

const translatedEntityIdsSlice = createSlice({
    name: SLICE_NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(
                translateEntityIds.pending,
                (state, { meta: { arg } }) => {
                    const { from, to, events = [], selections = [] } = arg;
                    const { inFlight } = ensureDirection(state, getDirectionKey(from, to));

                    events.forEach(id => { inFlight.events[id] = true; });
                    selections.forEach(id => { inFlight.selections[id] = true; });
                },
            )
            .addCase(
                translateEntityIds.fulfilled,
                (state, { payload }) => {
                    const {
                        from,
                        to,
                        requestedEvents,
                        requestedSelections,
                        events,
                        selections,
                    } = payload;

                    const directionMaps = ensureDirection(state, getDirectionKey(from, to));
                    const reverseMaps = ensureDirection(state, getDirectionKey(to, from));

                    requestedEvents.forEach(id => {
                        const translatedId = events[id] ?? null;
                        directionMaps.events[id] = translatedId;
                        delete directionMaps.inFlight.events[id];
                        if (translatedId !== null) {
                            reverseMaps.events[translatedId] = id;
                        }
                    });
                    requestedSelections.forEach(id => {
                        const translatedId = selections[id] ?? null;
                        directionMaps.selections[id] = translatedId;
                        delete directionMaps.inFlight.selections[id];
                        if (translatedId !== null) {
                            reverseMaps.selections[translatedId] = id;
                        }
                    });
                },
            )
            .addCase(
                translateEntityIds.rejected,
                (state, { meta: { arg } }) => {
                    const { from, to, events = [], selections = [] } = arg;
                    const { inFlight } = ensureDirection(state, getDirectionKey(from, to));

                    events.forEach(id => { delete inFlight.events[id]; });
                    selections.forEach(id => { delete inFlight.selections[id]; });
                },
            );
    },
});

export default translatedEntityIdsSlice.reducer;
